import { FC, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import {
  getInternalDeliveryFacilityLockGetFacilityLocksQueryKey,
  useInternalDeliveryFacilityLockGetFacilityLocks,
  useInternalDeliveryFacilityLockLockFacility,
  useInternalDeliveryFacilityLockUnlockFacility,
} from 'api/internal-delivery-facility-lock/internal-delivery-facility-lock';
import styled, { css } from 'styled-components';
import TextButton from 'components/inputs/TextButton';
import ConfirmModal from 'components/Modal/ConfirmModal';
import useModalStack from 'contexts/modal/useModalStack';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';

// Ikonen ska sitta på textraden: ingen knapputfyllnad, samma radhöjd som namnet och fast
// bredd så att låsen hamnar i en rak kolumn oavsett låst/öppen ikon (olika breda).
const LockSlot = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  line-height: 1;
`;

const LockButton = styled(TextButton)`
  ${LockSlot}
`;

const LockIndicator = styled.span`
  ${LockSlot}
`;

interface Props {
  companyId: number;
  companyName: string;
}

const formatTime = (date: Date) =>
  date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

/**
 * Anläggningslåset: låst anläggning tar inte emot fler annonsförfrågningar resten av dagen.
 * Alla som ser överblicken ser låset; bara AnnonsAdmin kan låsa och låsa upp. Låset gäller
 * alltid i dag, oavsett vilken vecka överblicken visar, och släpper av sig självt vid midnatt.
 */
const FacilityLockButton: FC<Props> = ({ companyId, companyName }) => {
  const me = useMe();
  const modalStack = useModalStack();
  const queryClient = useQueryClient();
  const canEdit = !!me && includesRoles([Roles.AnnonsAdmin], me.roles);

  // Samma frågenyckel för alla rader, så react-query hämtar låsen en gång för hela tabellen.
  const locks = useInternalDeliveryFacilityLockGetFacilityLocks(undefined, {
    query: { staleTime: 1000 * 30 },
  });
  const lockFacility = useInternalDeliveryFacilityLockLockFacility();
  const unlockFacility = useInternalDeliveryFacilityLockUnlockFacility();

  const lock = useMemo(
    () => locks.data?.find((l) => l.companyId === companyId),
    [locks.data, companyId]
  );
  const isBusy = lockFacility.isLoading || unlockFacility.isLoading;

  if (!lock && !canEdit) {
    return null;
  }

  const refresh = () =>
    queryClient.invalidateQueries(
      getInternalDeliveryFacilityLockGetFacilityLocksQueryKey()
    );

  const title = lock
    ? `Låst för annonsförfrågningar resten av dagen — av ${
        lock.lockedByName ?? 'okänd'
      } kl ${formatTime(lock.lockedAt)}.${canEdit ? ' Klicka för att låsa upp.' : ''}`
    : 'Lås anläggningen för annonsförfrågningar resten av dagen';

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit || isBusy) return;

    if (lock) {
      unlockFacility.mutate({ params: { companyId } }, { onSuccess: refresh });
      return;
    }

    modalStack.push(
      <ConfirmModal
        title={`Lås ${companyName}?`}
        content="Inga nya annonsförfrågningar till anläggningen kan läggas resten av dagen. Redan lagda förfrågningar påverkas inte. Låset släpper vid midnatt, eller när du låser upp."
        confirmText="Lås"
        onConfirm={() => {
          lockFacility.mutate(
            { params: { companyId } },
            { onSuccess: refresh }
          );
          modalStack.pop();
        }}
        onCancel={() => modalStack.pop()}
      />
    );
  };

  const icon = (
    <FontAwesomeIcon
      icon={lock ? faLock : faLockOpen}
      color={lock ? 'crimson' : undefined}
      style={lock ? undefined : { opacity: 0.3 }}
    />
  );

  if (!canEdit) {
    return <LockIndicator title={title}>{icon}</LockIndicator>;
  }

  return (
    <LockButton unpadded onClick={onClick} title={title} disabled={isBusy}>
      {icon}
    </LockButton>
  );
};

export default FacilityLockButton;
